import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { WebsiteContent, WebsiteContentVersion, WebsiteName, ContentStatus } from '@/types/website';

export function useWebsiteContent(websiteFilter?: WebsiteName) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: content = [], isLoading, error } = useQuery({
    queryKey: ['website-content', websiteFilter],
    queryFn: async () => {
      let query = supabase
        .from('website_content')
        .select(`
          *,
          created_profile:profiles!website_content_created_by_fkey(display_name, email),
          editor_profile:profiles!website_content_last_edited_by_fkey(display_name, email)
        `)
        .order('updated_at', { ascending: false });

      if (websiteFilter) {
        query = query.eq('website', websiteFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as WebsiteContent[];
    },
    enabled: !!user,
  });

  const createContent = useMutation({
    mutationFn: async (item: {
      title: string;
      slug: string;
      website: WebsiteName;
      content?: string;
    }) => {
      const { data, error } = await supabase
        .from('website_content')
        .insert({
          ...item,
          created_by: user?.id,
          last_edited_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-content'] });
      toast.success('Content aangemaakt');
    },
    onError: (error: any) => {
      console.error('Error creating content:', error);
      if (error.code === '23505') {
        toast.error('Er bestaat al content met deze slug voor deze website');
      } else {
        toast.error('Fout bij aanmaken content');
      }
    },
  });

  const updateContent = useMutation({
    mutationFn: async ({ id, createVersion, versionNotes, ...updates }: Partial<WebsiteContent> & { 
      id: string; 
      createVersion?: boolean;
      versionNotes?: string;
    }) => {
      // If creating a version, first save the current content as a version
      if (createVersion) {
        const { data: current } = await supabase
          .from('website_content')
          .select('content')
          .eq('id', id)
          .single();

        if (current) {
          await supabase
            .from('website_content_versions')
            .insert({
              content_id: id,
              content: current.content,
              version_notes: versionNotes || 'Automatische versie',
              created_by: user?.id,
            });
        }
      }

      const { data, error } = await supabase
        .from('website_content')
        .update({
          ...updates,
          last_edited_by: user?.id,
          published_at: updates.status === 'live' ? new Date().toISOString() : undefined,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-content'] });
      queryClient.invalidateQueries({ queryKey: ['website-content-versions'] });
      toast.success('Content bijgewerkt');
    },
    onError: (error) => {
      console.error('Error updating content:', error);
      toast.error('Fout bij bijwerken content');
    },
  });

  const deleteContent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('website_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-content'] });
      toast.success('Content verwijderd');
    },
    onError: (error) => {
      console.error('Error deleting content:', error);
      toast.error('Fout bij verwijderen content');
    },
  });

  return {
    content,
    isLoading,
    error,
    createContent,
    updateContent,
    deleteContent,
  };
}

export function useWebsiteContentVersions(contentId: string | undefined) {
  const { user } = useAuth();

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['website-content-versions', contentId],
    queryFn: async () => {
      if (!contentId) return [];

      const { data, error } = await supabase
        .from('website_content_versions')
        .select(`
          *,
          created_profile:profiles!website_content_versions_created_by_fkey(display_name, email)
        `)
        .eq('content_id', contentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as WebsiteContentVersion[];
    },
    enabled: !!user && !!contentId,
  });

  return { versions, isLoading };
}
